import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useState,
} from 'react';

import useQueryClient from 'hooks/useQueryClient';
import useRatings from 'hooks/useRatings';
import useToast from 'hooks/useToast';

import {
  addActionToComment,
  getDislikedComment,
  getLikedComment,
  isCommentContentBlocked,
} from 'lib/comments';
import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { commentQueryKeys, reviewQueryKeys } from 'lib/queryKeys';
import { formatCommentAsReview } from 'lib/reviews';
import { noop } from 'lib/utils';

import DomainProvider, { useDomain } from 'providers/DomainProvider';
import { useUser } from 'providers/UserProvider';

import {
  deleteComment as delComment,
  patchEditComment,
  postArticleComment,
  postOfferComment,
  postThreadComment,
} from 'services/comment';

import COMMENT from 'constants/comment';

const MIN_COMMENTS_LENGTH_TO_SHOW_FOOTER = 2;

const CommentsRootContext = createContext({
  activeAnswerCommentId: '',
  commentsCount: 0,
  commentsSort: '',
  isCommentsListEmpty: null,
  isFooterActive: true,
  gaEventCategory: '',
  addLike: noop,
  addDislike: noop,
  createComment: noop,
  deleteComment: noop,
  dispatchCommentsCount: noop,
  editComment: noop,
  setActiveAnswerCommentId: noop,
  setCommentsSort: noop,
  setCommentsListEmpty: noop,
});

const POST_COMMENT_REQUEST_BY_DOMAIN = {
  article: postArticleComment,
  offer: postOfferComment,
  thread: postThreadComment,
};

const CommentsRoot = ({
  children,
  gaEventCategory,
  initialSort = COMMENT.SORT_VALUES.OLDEST,
  onLike,
  onDislike,
}) => {
  const queryClient = useQueryClient();
  const { domain, domainId, domainIdKey } = useDomain();
  const { showToast } = useToast();
  const { loadingLoggedInUser, isLoggedIn, runCallbackIfLoggedIn } = useUser();

  const [activeAnswerCommentId, setActiveAnswerCommentId] = useState(null);
  const [commentsSort, setCommentsSort] = useState(initialSort);
  const [isCommentsListEmpty, setCommentsListEmpty] = useState(false);
  const [commentsCount, dispatchCommentsCount] = useReducer((state, action) => {
    let result = 0;

    switch (action.type) {
      case 'create':
        result = state + 1;
        break;
      case 'delete':
        result = state - 1;
        break;
      case 'populate':
        result = action.count;
        break;
    }

    queryClient.setQueryData(
      commentQueryKeys[domain].count({ [domainIdKey]: domainId }),
      result
    );

    return result;
  }, 0);

  const onDislikeHandler = useCallback(
    (id, action) => {
      queryClient.setInfiniteQueryData(
        commentQueryKeys[domain].list({
          [domainIdKey]: domainId,
          sortType: commentsSort,
        }),
        (pages) =>
          addActionToComment({
            action,
            commentId: id,
            pages,
            handleActionCallback: getDislikedComment,
          })
      );

      if (onDislike) {
        onDislike(id, action);
      }
    },
    [domainId, queryClient, commentsSort, onDislike]
  );

  const onLikeHandler = useCallback(
    (id, action) => {
      queryClient.setInfiniteQueryData(
        commentQueryKeys[domain].list({
          [domainIdKey]: domainId,
          sortType: commentsSort,
        }),
        (pages) =>
          addActionToComment({
            action,
            commentId: id,
            pages,
            handleActionCallback: getLikedComment,
          })
      );

      if (onLike) {
        onLike(id, action);
      }
    },
    [domainId, queryClient, commentsSort, onLike]
  );

  const { addDislike, addLike } = useRatings({
    domain,
    domainId,
    onDislike: onDislikeHandler,
    onLike: onLikeHandler,
  });

  const { mutate: mutateCreateComment } = useMutation({
    mutationFn: ({ commentText, isParentAReview, parentId }) => {
      const data = { commentText, [domainIdKey]: domainId };

      if (!isParentAReview) {
        data.parentId = parentId;
      }

      return POST_COMMENT_REQUEST_BY_DOMAIN[domain](data);
    },
    onSuccess: (
      { comment: addedComment },
      { aliasId, isParentAReview, parentId }
    ) => {
      sendEvent({
        action: 'post_comment_success',
        category: gaEventCategory,
      });

      setActiveAnswerCommentId(null);
      dispatchCommentsCount({ type: 'create' });

      if (isParentAReview) {
        /**
         * As we simulates the behavior of replies in a review, we still need to
         * visually put the new comment as a reply of review to don't turn the
         * user experience too bad.
         */
        queryClient.setInfiniteQueryData(
          reviewQueryKeys.list({ aliasId, sort: COMMENT.REVIEWS_SORT }),
          (pages) =>
            pages.map((page) => ({
              ...page,
              data: page.data.map((review) => {
                if (review.id !== parentId) {
                  return review;
                }

                const updatedReview = { ...review };

                const addedCommentAsReview = formatCommentAsReview(
                  addedComment,
                  parentId
                );

                if (!updatedReview.replies) {
                  updatedReview.replies = [];
                }

                updatedReview.replies.push(addedCommentAsReview);

                return updatedReview;
              }),
            }))
        );
        return;
      }

      // Adds the newly added comment to current comments list
      queryClient.setInfiniteQueryData(
        commentQueryKeys[domain].list({
          [domainIdKey]: domainId,
          sortType: commentsSort,
        }),
        (pages) =>
          parentId
            ? pages.map((commentsPage) =>
                commentsPage.map((comment) => {
                  if (comment.commentId !== addedComment.parentId) {
                    return comment;
                  }

                  const newComment = { ...comment };

                  newComment.replies = newComment.replies.concat(addedComment);

                  return newComment;
                })
              )
            : pages.map((commentPage) => [addedComment, ...commentPage])
      );
    },
    useErrorBoundary: true,
  });

  const { mutate: mutateDeleteComment } = useMutation({
    mutationFn: delComment,
  });

  const { mutate: mutateEditComment } = useMutation({
    mutationFn: patchEditComment,
    onError: () => {
      showToast({
        text: placeholder('toasts.errors.comments.edited'),
        type: 'danger',
      });
    },
    onSuccess: (_, { parentId, commentId, commentText }) => {
      /**
       * For now is preferable modify the existing comment that is stored in
       * the query instead replace to the comment returned from API due the
       * data returned has a totally different modeling.
       */
      queryClient.setInfiniteQueryData(
        commentQueryKeys[domain].list({
          [domainIdKey]: domainId,
          sortType: commentsSort,
        }),
        (pages) =>
          parentId
            ? pages.map((commentsPage) =>
                commentsPage.map((comment) => {
                  if (comment.commentId !== parentId) {
                    return comment;
                  }

                  const newComment = comment;

                  newComment.replies = newComment.replies.map((reply) => {
                    if (reply.commentId !== commentId) {
                      return reply;
                    }

                    return { ...reply, edited: true, commentText };
                  });
                  return newComment;
                })
              )
            : pages.map((commentPage) =>
                commentPage.map((comment) => {
                  if (comment.commentId !== commentId) {
                    return comment;
                  }

                  return { ...comment, edited: true, commentText };
                })
              )
      );
      showToast({
        text: placeholder('toasts.successes.comments.edited'),
        type: 'success',
      });
      sendEvent({
        action: `${domain}__comment_edit_success`,
        category: gaEventCategory,
      });
    },
  });

  const createComment = useCallback(
    ({ aliasId, commentText, parentId }) => {
      const commentToCreate = { aliasId, commentText, parentId };

      /**
       * To increase the interactions of the offers, we are making a test that
       * injects the reviews inside comments list and due some technical issues
       * we can't directly reply a review. Visually we are allowing to users
       * reply these reviews, but internally we changes the flow to creates this
       * reply as a completely new comment.
       */
      if (aliasId && parentId) {
        const reviews = queryClient.getInfiniteQueryData(
          reviewQueryKeys.list({ aliasId, sort: COMMENT.REVIEWS_SORT }),
          'data'
        );
        const isParentCommentAReview = reviews.some(
          (review) => review.id === parentId
        );

        /**
         * This info it's just appended to comment data to manipulates the
         * infinite query to visually shows the created comment as a reply.
         */
        commentToCreate.isParentAReview = isParentCommentAReview;
      }

      sendEvent({
        action: !isLoggedIn ? 'comment_attempt_unlogged' : 'comment_attempt',
        category: gaEventCategory,
      });

      runCallbackIfLoggedIn(mutateCreateComment, commentToCreate);
    },
    [runCallbackIfLoggedIn]
  );

  const deleteComment = useCallback((commentId) => {
    mutateDeleteComment({ commentId });
    // Updates comment list without the deleted comment
    queryClient.setInfiniteQueryData(
      commentQueryKeys[domain].list({
        [domainIdKey]: domainId,
        sortType: commentsSort,
      }),
      (pages) =>
        pages.map((page) =>
          page.filter((comment) => {
            if (comment.replies.length > 0) {
              const newComment = structuredClone(comment);

              // Filter replies removing the deleted one
              newComment.replies = comment.replies.filter(
                (reply) => reply.commentId !== commentId
              );

              /**
               * If the comment has replies, it will not be removed from the
               * list. Instead, we change its statusName so we can render it
               * differently and keep the replies. The same behavior is
               * replicated at the API
               */
              if (comment.commentId === commentId) {
                newComment.commentStatusName = COMMENT.STATUSES.REMOVED_BY_USER;
              }

              /**
               * If comment doesn't have replies, and its content is currently
               * blocked, remove it from the list as well
               */
              if (
                newComment.replies.length === 0 &&
                isCommentContentBlocked(newComment.commentStatusName)
              ) {
                return false;
              }

              return newComment;
            }

            if (comment.commentId !== commentId) {
              return comment;
            }
          })
        )
    );

    dispatchCommentsCount({ type: 'delete' });
  }, []);

  const editComment = useCallback((commentToEdit) => {
    mutateEditComment(commentToEdit);
  }, []);

  const isFooterActive =
    commentsCount > MIN_COMMENTS_LENGTH_TO_SHOW_FOOTER &&
    !loadingLoggedInUser &&
    isLoggedIn;

  return (
    <DomainProvider domain={domain} domainId={domainId}>
      <CommentsRootContext.Provider
        value={{
          activeAnswerCommentId,
          commentsCount,
          isCommentsListEmpty,
          commentsSort,
          isFooterActive,
          gaEventCategory,
          addLike,
          addDislike,
          createComment,
          deleteComment,
          editComment,
          setActiveAnswerCommentId,
          dispatchCommentsCount,
          setCommentsListEmpty,
          setCommentsSort,
        }}
      >
        {children}
      </CommentsRootContext.Provider>
    </DomainProvider>
  );
};

export const useCommentsRoot = () => useContext(CommentsRootContext);

CommentsRoot.propTypes = {
  children: PropTypes.node.isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  initialSort: PropTypes.string,
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
};

export default CommentsRoot;
